import { useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { DataStructure } from './TreeView.types';

/**
 *
 * @param dataStructure DataStructure defining the idKey and buildLabel for each level
 * @param id string that allows this data to be differentiated from other uses of the same hook in other places
 * @param disableLocalStorage prevents tree view states from persisting across session
 * @returns { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, reset } props to pass into the TreeView component and a `reset` function to call to clear the checkboxes/expanded state
 */
const useTreeViewRowStates = (dataStructure: DataStructure[], id: string, disableLocalStorage: boolean = false) => {
  const checkedRows = useRef<any[]>([]);
  const expandedRows = useRef<any[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    const checkedRowsFromStorage = localStorage.getItem(`${id}CheckedRows`);
    const expandedRowsFromStorage = localStorage.getItem(`${id}ExpandedRows`);
    if (checkedRowsFromStorage && !disableLocalStorage) checkedRows.current = JSON.parse(checkedRowsFromStorage);
    if (expandedRowsFromStorage && !disableLocalStorage) expandedRows.current = JSON.parse(expandedRowsFromStorage);
    setHasUpdate(!hasUpdate);
    return () => {
      !disableLocalStorage && localStorage.setItem(`${id}CheckedRows`, JSON.stringify(checkedRows.current));
      !disableLocalStorage && localStorage.setItem(`${id}ExpandedRows`, JSON.stringify(expandedRows.current));
    };
  }, []);

  const handleCheckboxClick = (row: any) => {
    const idKey = dataStructure[row.level].idKey;
    const rowsBeingRemoved: any[] = [];

    // if row is checked and doesn't already exist in checkedRows
    if (
      row.rowChecked &&
      checkedRows.current.findIndex((checkedRow: any) => {
        if (
          checkedRow[idKey] === row[idKey] &&
          checkedRow.level === row.level &&
          isEqual(checkedRow.parentRows, row.parentRows)
        ) {
          return true;
        } else {
          return false;
        }
      }) === -1
    ) {
      // add row to checkedRows
      checkedRows.current = [...checkedRows.current, row];
      setHasUpdate(!hasUpdate);
      // if row is not checked
    } else if (!row.rowChecked) {
      // remove row from checkedRows
      const rowToRemove = checkedRows.current.findIndex((checkedRow: any) => {
        if (
          checkedRow[idKey] === row[idKey] &&
          checkedRow.level === row.level &&
          isEqual(checkedRow.parentRows, row.parentRows)
        )
          return true;
        return false;
      });
      let newCheckedRows = [...checkedRows.current];
      if (rowToRemove !== -1) {
        const rowRemoved = newCheckedRows.splice(rowToRemove, 1)[0];
        // store the row being removed so we can remove it's children
        rowsBeingRemoved.push(rowRemoved);
      }

      while (rowsBeingRemoved.length > 0) {
        const parentRow = rowsBeingRemoved.pop();
        newCheckedRows = newCheckedRows.filter((checkedRow) => {
          const parentLevel = parentRow.level;
          const parentIdKey = dataStructure[parentLevel].idKey;
          let isEveryParentEquals = row.parentRows.every((element: any,  index: number) => {
            return element.id === checkedRow.parentRows[index]?.id;
          });
          if (isEveryParentEquals) {
            rowsBeingRemoved.push(checkedRow);
            return false;
          }
          if (checkedRow.parentId !== parentRow[parentIdKey]) {
            // keep a row in checkedRows if it's parent is not the row that was unchecked
            return true;
          } else if (
            // keep a row in checkedRows if it's parentRows is different from the parentRows of the row that was unchecked
            // removing the parentRow from the parentRows of the checkedRow
            !isEqual(
              parentRow.parentRows,
              checkedRow.parentRows.filter((row: any) => row.level !== parentRow.level + 1)
            )
          ) {
            return true;
          } else {
            // if the row in checkedRows has the same parentId as the row being removed and it's parentRows match, remove it and add it to the list of rows being removed
            rowsBeingRemoved.push(checkedRow);
            return false;
          }
        });
      }

      // remove children of children that are removed
      checkedRows.current = newCheckedRows;
      // checkedRows.current = arrayWithChildrenRemoved;
      setHasUpdate(!hasUpdate);
    }
  };

  const handleRowClick = (row: any) => {
    const idKey = dataStructure[row.level].idKey;
    if (row.rowExpanded) {
      expandedRows.current.push(row);
      setHasUpdate(!hasUpdate);
    } else {
      const rowToRemove = expandedRows.current.findIndex((expandedRow: any) => {
        if (
          expandedRow[idKey] === row[idKey] &&
          expandedRow.level === row.level &&
          isEqual(expandedRow.parentRows, row.parentRows)
        )
          return true;
        return false;
      });
      const newExpandedRows = [...expandedRows.current];
      if (rowToRemove !== -1) newExpandedRows.splice(rowToRemove, 1);
      expandedRows.current = newExpandedRows;
      setHasUpdate(!hasUpdate);
    }
  };

  const handleUpwardsDeselectPropagation = (row: any) => {
    const { idKey: parentIdKey } = dataStructure[row.level - 1] || { idKey: false };
    if (parentIdKey) {
      let rowParentIndex = checkedRows.current.findIndex((checkedRow) => {
        /** every parent element should match: false = Pizza -> Pan -> Small -> Base === Pizza -> Thin -> Small -> Base */
        let isParentsEquals = checkedRow.parentRows.every((element: any,  index: number) => {
          return element.id === row.parentRows[index]?.id;
        });
        return checkedRow[parentIdKey] === row.parentRows[checkedRow.level]?.id && checkedRow.level === row.level - 1 && isParentsEquals;
      });
      if (rowParentIndex !== -1) {
        handleUpwardsDeselectPropagation(checkedRows.current.splice(rowParentIndex, 1).at(0));
      }
    }
  }

  const reset = () => {
    checkedRows.current = [];
    expandedRows.current = [];
    setHasUpdate(!hasUpdate);
  };

  return {
    checkedRows: checkedRows.current,
    expandedRows: expandedRows.current,
    handleRowClick,
    handleCheckboxClick,
    handleUpwardsDeselectPropagation,
    reset,
  };
};

export default useTreeViewRowStates;
